import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Calendar, Download, ArrowUpFromLine, Loader2, CalendarDays } from 'lucide-react'
import { listCalendarEvents, importCalendarEvents, exportPointsToCalendar, CalendarEvent } from '../../shared/api/calendar'

interface Props {
  tripId: string
  onClose: () => void
  /** Current trip point IDs — for export */
  pointIds?: string[]
  onImported?: (count: number) => void
  onExported?: (count: number) => void
}

export default function CalendarSyncPanel({ tripId, onClose, pointIds = [], onImported, onExported }: Props) {
  const [mode, setMode] = useState<'import' | 'export'>('import')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const qc = useQueryClient()

  const { data: events = [], isLoading, isError } = useQuery({
    queryKey: ['calendar-events', tripId],
    queryFn: () => listCalendarEvents(tripId),
    enabled: mode === 'import',
  })

  const importMut = useMutation({
    mutationFn: (ids: string[]) => importCalendarEvents(tripId, ids),
    onSuccess: (imported) => {
      qc.invalidateQueries({ queryKey: ['trip-points', tripId] })
      onImported?.(imported.length)
      onClose()
    },
  })

  const exportMut = useMutation({
    mutationFn: (ids: string[]) => exportPointsToCalendar(tripId, ids),
    onSuccess: (exported) => {
      const ok = exported.filter((e) => e.success).length
      onExported?.(ok)
      onClose()
    },
  })

  const toggleAll = (items: string[]) => {
    if (items.every((id) => selected.has(id))) {
      setSelected(new Set())
    } else {
      setSelected(new Set(items))
    }
  }

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2 text-gray-900 font-semibold">
            <CalendarDays className="w-5 h-5 text-blue-600" />
            Google Calendar Sync
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b text-sm font-medium">
          {(['import', 'export'] as const).map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setSelected(new Set()) }}
              className={`flex-1 py-3 capitalize transition ${
                mode === m
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {m === 'import' ? (
                <span className="flex items-center justify-center gap-1.5">
                  <Download className="w-4 h-4" /> Import from Calendar
                </span>
              ) : (
                <span className="flex items-center justify-center gap-1.5">
                  <ArrowUpFromLine className="w-4 h-4" /> Export to Calendar
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {mode === 'import' ? (
            <>
              {isLoading && (
                <div className="flex items-center justify-center h-40 text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  Loading events…
                </div>
              )}
              {isError && (
                <p className="text-center text-sm text-red-500 py-8">
                  Could not load Calendar. Is your Google account connected?
                </p>
              )}
              {!isLoading && !isError && events.length === 0 && (
                <p className="text-center text-sm text-gray-400 py-8">
                  No events found in the trip date range.
                </p>
              )}
              {!isLoading && events.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-500">{events.length} events found</span>
                    <button
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => toggleAll(events.map((e) => e.event_id))}
                    >
                      {events.every((e) => selected.has(e.event_id)) ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <ul className="space-y-2">
                    {events.map((ev) => (
                      <CalEventRow
                        key={ev.event_id}
                        event={ev}
                        checked={selected.has(ev.event_id)}
                        onToggle={() => toggle(ev.event_id)}
                      />
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : (
            <>
              {pointIds.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-8">
                  No trip points to export yet. Add some points first.
                </p>
              ) : (
                <p className="text-sm text-gray-600 bg-blue-50 rounded-lg p-4">
                  <Calendar className="w-4 h-4 inline mr-1 text-blue-500" />
                  This will create Google Calendar events for all <strong>{pointIds.length}</strong> trip
                  point{pointIds.length !== 1 ? 's' : ''}.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          {mode === 'import' ? (
            <button
              disabled={selected.size === 0 || importMut.isPending}
              onClick={() => importMut.mutate([...selected])}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {importMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Import {selected.size > 0 ? `${selected.size} event${selected.size > 1 ? 's' : ''}` : ''}
            </button>
          ) : (
            <button
              disabled={pointIds.length === 0 || exportMut.isPending}
              onClick={() => exportMut.mutate(pointIds)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              {exportMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Export {pointIds.length} point{pointIds.length !== 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function CalEventRow({ event, checked, onToggle }: {
  event: CalendarEvent
  checked: boolean
  onToggle: () => void
}) {
  return (
    <li
      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
        checked ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onToggle}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        className="mt-0.5 accent-blue-600"
        aria-label={`Select ${event.summary}`}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{event.summary}</p>
        {event.location && (
          <p className="text-xs text-gray-500 truncate">📍 {event.location}</p>
        )}
        <p className="text-xs text-gray-400">
          {event.is_all_day ? '📅 ' : '🕐 '}
          {event.start}{event.end && event.end !== event.start ? ` → ${event.end}` : ''}
        </p>
      </div>
    </li>
  )
}
